import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select } from 'antd';
import { nonAccentVietnamese } from '@app/utils/utils';
import { getList } from '@app/apis/schools';
import _locationApis from '@app/apis/locations/location';
import { useDeepEffect } from '@app/hooks/useDeepEffect';
import locationUtils from '@app/utils/location.utils';

const SchoolListSelect = ({
    label = null,
    defaultValue,
    onChange,
    disabled = false,
}) => {
    const [list, setList] = useState([]);
    const [selectId, setSelectId] = useState('');
    const [listLocation, setListLocation] = useState([]);

    // ── Fetch danh sách trường ──
    useEffect(() => {
        let cancelled = false;

        getList().then(({ status, data }) => {
            if (cancelled) return;

            if (status !== 200 || !data?.data?.length) {
                setList([]);
                setSelectId('');
                return;
            }

            const dataRes = data.data;
            const found =
                defaultValue &&
                dataRes.some((item) => item.o_id === defaultValue);
            const id = found ? defaultValue : dataRes[0].o_id;

            setList(dataRes);
            setSelectId(id);
            onChange?.(id);
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Fetch location khi list thay đổi ──
    useDeepEffect(() => {
        if (!list?.length) return;

        const locationIdSet = new Set();
        list.forEach((o) => {
            const parts = o.l_id.split('.');
            locationIdSet.add(o.l_id);
            if (parts.length >= 2) locationIdSet.add(`${parts[0]}.${parts[1]}`);
            if (parts.length >= 1) locationIdSet.add(parts[0]);
        });

        _locationApis
            .getByListId([...locationIdSet])
            .then(({ status, data }) => {
                if (status !== 200) {
                    setListLocation([]);
                    return;
                }
                setListLocation(data.data);
            });
    }, [list]);

    // ── Danh sách trường + tên địa phương ──
    const listView = useMemo(() => {
        if (!list?.length || !listLocation?.length) return [];
        return list.map((elm) => ({
            ...elm,
            locationFullName: locationUtils.getFullNameById(
                elm.l_id,
                listLocation,
            ),
        }));
    }, [list, listLocation]);

    // ── Đồng bộ khi defaultValue thay đổi từ bên ngoài ──
    useEffect(() => {
        if (defaultValue) {
            setSelectId(defaultValue);
        } else if (list.length > 0) {
            setSelectId(list[0].o_id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue]);

    // ── Filter tìm kiếm tiếng Việt không dấu ──
    const filterOption = useCallback((input, option) => {
        if (!option?.children?.[0]) return false;
        const normalizedOption = nonAccentVietnamese(
            option.children[0].replace(/\s/g, ''),
        ).toLowerCase();
        const normalizedInput = nonAccentVietnamese(
            input.replace(/\s/g, ''),
        ).toLowerCase();
        return normalizedOption.includes(normalizedInput);
    }, []);

    // ── Options memo ──
    const options = useMemo(() => {
        if (!listView.length) return null;
        return listView.map((elm) => (
            <Select.Option value={elm.o_id} key={elm.o_id}>
                {elm.o_name}
                <p style={{ fontSize: '10px', lineHeight: '1' }}>
                    {elm.locationFullName}
                </p>
            </Select.Option>
        ));
    }, [listView]);

    const handleChange = useCallback(
        (value) => {
            setSelectId(value);
            onChange?.(value);
        },
        [onChange],
    );

    return (
        <>
            {label && (
                <>
                    <label>
                        <h6 className="panel-title">{label}</h6>
                    </label>
                    <div className="clearfix mb20" />
                </>
            )}
            <Select
                value={selectId}
                optionFilterProp="children"
                filterOption={filterOption}
                disabled={disabled}
                className="disable-modified"
                onChange={handleChange}
                showSearch
            >
                {options}
            </Select>
        </>
    );
};

export default SchoolListSelect;
